import * as React from "react";
import { Country } from "../types";
import VirtualList from 'react-tiny-virtual-list';
import memorize from "../common/memorize";
import t from "../i18n";
import Img from "../Controls/Img";
import { scrollWindow } from "../utils";

function searchCountriesFn(countries: Country[], search: string) {
    if (search) {
        return countries.filter(c => c.name.toLowerCase().indexOf(search.toLowerCase()) > -1);
    } else {
        return countries;
    }
}

declare type CountryListProps = {
    countries: Country[],
    selectedCountry: Country | undefined;
    onCountrySelected: (country: Country | undefined) => void;
}
declare type CountryListState = {
    highlightIndex: number;
    searchCountryName: string;
}
export default class CountryList extends React.Component<CountryListProps, CountryListState> {
    readonly state: CountryListState = { highlightIndex: -1, searchCountryName: "" };
    readonly searchCountriesFn = memorize(searchCountriesFn);
    _searchInput: HTMLInputElement | null;
    assignSearchRef = (searchInput: HTMLInputElement | null) => {
        this._searchInput = searchInput;
    }
    onCountrySelected = (country: Country) => {
        if (!this.props.onCountrySelected) throw new Error("onCountrySelected must be passed");
        this.setState({ highlightIndex: -1 });
        this.props.onCountrySelected(country);
    }
    closeMe = (event: Event) => {
        const inCountryList = (event.target as HTMLElement).closest(".country-list");
        if (inCountryList) return;
        if (!this.props.onCountrySelected) throw new Error("onCountrySelected must be passed");
        this.props.onCountrySelected(this.props.selectedCountry);
    }
    keyDown = (event: KeyboardEvent) => {
        const countries = this.searchCountriesFn.exec(this.props.countries, this.state.searchCountryName);
        if (event.keyCode === 40) {    //key: ArrowDown
            event.preventDefault();
            let newIndex: number;
            if (this.state.highlightIndex >= countries.length - 1) newIndex = countries.length - 1;
            else newIndex = this.state.highlightIndex + 1;
            this.setState({ highlightIndex: newIndex });
        } else if (event.keyCode === 38) { //key: ArrowUp
            event.preventDefault();
            let newIndex: number;
            if (this.state.highlightIndex <= 0) newIndex = 0;
            else newIndex = this.state.highlightIndex - 1;
            this.setState({ highlightIndex: newIndex });
        } else if (event.keyCode === 27) {  //key: Escape
            this.props.onCountrySelected(this.props.selectedCountry);
        } else if ([9, 13].indexOf(event.keyCode) > -1) {   //Tab: 9, Enter: 13, 
            const country = countries[this.state.highlightIndex === -1 ? 0 : this.state.highlightIndex];
            this.onCountrySelected(country);
        }
    }
    setSearchCountry = (e: React.ChangeEvent<HTMLInputElement>) => {
        const countries = this.searchCountriesFn.exec(this.props.countries, this.state.searchCountryName);
        let newHightlightIndex: number = this.state.highlightIndex;
        if (newHightlightIndex >= countries.length) newHightlightIndex = countries.length - 1;
        else if (newHightlightIndex <= 0) newHightlightIndex = 0;
        this.setState({ searchCountryName: e.target.value, highlightIndex: newHightlightIndex });
    }
    setHighlight = (index: number, e: React.MouseEvent<HTMLDivElement>) => {
        if (this.state.highlightIndex === index) return;
        this.setState({ highlightIndex: index });
    }
    componentDidMount() {
        if (this._searchInput) this._searchInput.focus();
        const mobileNumberControl = document.getElementById("mobile-number-label");
        if (mobileNumberControl && document.body.offsetWidth <= 576) {
            scrollWindow(mobileNumberControl.offsetTop, 1000);
        }
        if (this.props.selectedCountry) {
            const countries = searchCountriesFn(this.props.countries, this.state.searchCountryName);
            const index = countries.findIndex(c => c.countryCode === this.props.selectedCountry!.countryCode);
            this.setState({ highlightIndex: index });
        } else {
            this.setState({ highlightIndex: 0 });
        }
        document.addEventListener("mouseup", this.closeMe);
        document.addEventListener("keydown", this.keyDown);
    }
    componentWillUnmount() {
        document.removeEventListener("mouseup", this.closeMe)
        document.removeEventListener("keydown", this.keyDown);
    }
    render() {
        const selectedCountry = this.props.selectedCountry;
        const selectedIndex = selectedCountry ? this.props.countries.findIndex(c => c.countryCode === selectedCountry.countryCode) : -1;
        const scrollToIndex = this.state.highlightIndex === -1 ? selectedIndex : this.state.highlightIndex;
        const countries = this.searchCountriesFn.exec(this.props.countries, this.state.searchCountryName);
        const countryCount = countries.length;
        return (
            <React.Fragment>
                <input ref={this.assignSearchRef} className="form-control" type="text"
                    style={{marginBottom: 2}} 
                    placeholder={t.topup("search_country")}
                    value={this.state.searchCountryName}
                    onChange={this.setSearchCountry}
                />
                {countryCount === 0 && <div>{t.topup("no_country_found")}</div>}
                {countryCount > 0 && <VirtualList
                    scrollToIndex={scrollToIndex}
                    scrollToAlignment={"auto" as any}
                    itemCount={countries.length}
                    height={36 * Math.min(5, countryCount)}
                    width="100%"
                    itemSize={36}
                    renderItem={({ index, style }) => {
                        const c = countries[index];
                        const isSelected = this.props.selectedCountry ? this.props.selectedCountry.countryCode === c.countryCode : false;
                        const isHighlighted = index === this.state.highlightIndex;
                        const computedItemStyle = isSelected ? selectedItemStyle : isHighlighted ? highlightItemStyle : itemStyle;
                        return <div id={`eto-${index}`}
                            onMouseEnter={e => this.setHighlight(index, e)}
                            onMouseLeave={e => this.setHighlight(index, e)}
                            onMouseOver={e => this.setHighlight(index, e)}
                            key={c.countryCode}
                            className="d-flex align-items-center"
                            style={{ ...computedItemStyle, ...style }}
                            onClick={e => this.onCountrySelected(c)}>
                            <Img style={imageStyle} src={`/images/1x/countries-mobile/${c.countryCode}.jpg`} />
                            <div style={textStyle}>{c.name}</div>
                        </div>
                    }}
                />}
            </ React.Fragment>
        );
    }
}
const itemStyle: React.CSSProperties = {
    cursor: "default",
    padding: "7px 5px",
}
const highlightItemStyle: React.CSSProperties = {
    ...itemStyle,
    backgroundColor: "#ffe6e6"
}
const selectedItemStyle: React.CSSProperties = {
    ...itemStyle,
    backgroundColor: 'rgb(0, 122, 255)',
    color: "#fff"
}
const imageStyle: React.CSSProperties = {
    width: 24,
    height: 16,
    marginRight: 5,
}
const textStyle: React.CSSProperties = {
    whiteSpace: "nowrap",
    flex: 1,
    textOverflow: "ellipsis",
    overflow: "hidden",
}